import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBButton, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Target } from "lucide-react";

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_date: "", progress: 0, member_ids: [] });

  const load = async () => {
    const { data } = await api.get("/api/goals");
    setGoals(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/goals", form);
      toast.success("Goal added!");
      setOpen(false);
      setForm({ title: "", description: "", target_date: "", progress: 0, member_ids: [] });
      load();
    } catch { toast.error("Failed"); }
  };

  const updateProgress = async (g, progress) => {
    await api.patch(`/api/goals/${g.id}`, { ...g, progress });
    load();
  };

  const remove = async (id) => { await api.delete(`/api/goals/${id}`); load(); };

  const colors = ["#E8B96B", "#B8CFA9", "#E9B5A0", "#A8BFD1"];

  return (
    <div data-testid="goals-page">
      <PageHeader
        title="Family Goals"
        subtitle="Big dreams, small steps. Track them together."
        color="#E9B5A0"
        action={<NBButton testId="add-goal-btn" color="#E9B5A0" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Goal</NBButton>}
      />

      {goals.length === 0 ? (
        <EmptyState title="No goals yet" subtitle="Set a family goal to work towards together." />
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {goals.map((g, i) => (
            <NBCard key={g.id} color={colors[i % colors.length]} data-testid={`goal-${g.id}`}>
              <div className="flex justify-between items-start">
                <Target strokeWidth={2.5} />
                <button onClick={() => remove(g.id)} className="nb-btn bg-white p-1.5" data-testid={`delete-goal-${g.id}`}>
                  <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                </button>
              </div>
              <h4 className="text-2xl font-black mt-2" style={{ fontFamily: "var(--hh-display)" }}>{g.title}</h4>
              {g.description && <p className="text-sm mt-2">{g.description}</p>}
              {g.target_date && <p className="text-xs font-bold mt-2">🎯 By {g.target_date}</p>}

              <div className="mt-4">
                <div className="flex justify-between text-xs font-black mb-1">
                  <span>PROGRESS</span>
                  <span>{g.progress}%</span>
                </div>
                <div className="h-4 bg-white border-2 border-black rounded-full overflow-hidden">
                  <div className="h-full bg-black" style={{ width: `${g.progress}%` }} />
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={g.progress}
                  onChange={(e) => updateProgress(g, parseInt(e.target.value))}
                  className="w-full mt-2"
                  data-testid={`progress-${g.id}`}
                />
              </div>
              {g.completed && <span className="nb-badge bg-[#B8CFA9] mt-3 inline-block">✓ COMPLETED</span>}
            </NBCard>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Goal</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="goal-title" required placeholder="e.g. Save $5000 for vacation" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBTextarea testId="goal-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <NBInput testId="goal-date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
              <NBButton testId="submit-goal" type="submit" color="#E9B5A0" className="w-full">Save Goal</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
